/**
 * Pagina /tesoreria/gastos — Sprint 17 Fase 2.
 *
 * Sprint 17 S17-Z15 (forzando rebuild Vercel 2026-05-26):
 *   anular gasto usa <ModalConfirmar /> en lugar de window.prompt().
 *
 * Registro y consulta de gastos del barrio. Tabla historica + form
 * inline + 3 KPIs (Total mes, Total año, Top categorías).
 *
 * Gastos con compensacion_id != null se marcan con badge COMP — son
 * pagos en especie (Sprint 12) que NO tocan el banco real.
 */
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  anularGasto,
  crearGasto,
  getResumenGastos,
  listGastos,
  type GastoRead,
  type GastoResumen,
} from '../../lib/api/gastos';
import { listCategorias } from '../../lib/api/categorias';
import { CategoriaSelector } from '../../components/tesoreria/CategoriaSelector';
import { ModalEditarGasto } from '../../components/tesoreria/ModalEditarGasto';
import type { CategoriaRead } from '../../lib/api/types';
import { useAuth } from '../../lib/auth/AuthContext';
import { Card } from '../../components/ui/Card';
import { InputFecha } from '../../components/ui/InputFecha';
import { InputMontoCLP } from '../../components/ui/InputMontoCLP';
import { ModalConfirmar } from '../../components/ui/ModalConfirmar';
import { formatCLP, formatFecha } from '../../lib/format';
import { Monto, Num } from '../../components/tesoreria/Monto';
import { hoy } from '../../lib/tiempo';

export interface GastosViewProps {
  gastos: GastoRead[];
  categorias: CategoriaRead[];
  /** KPIs calculados en backend (año contable, Santiago, sin terceros). */
  resumen: GastoResumen | null;
  loading: boolean;
  error: string | null;
  onCrear: (input: {
    fecha: string;
    categoria_id: string;
    monto: number;
    proveedor: string;
    descripcion: string;
    comprobante_url: string | null;
  }) => Promise<void>;
  onAnular: (id: string, motivo: string) => Promise<void>;
  /** Sprint 17 S17-Z14: callback tras edición exitosa para refrescar lista. */
  onActualizado: (g: GastoRead) => void;
}

export function GastosView({
  gastos,
  categorias,
  resumen,
  loading,
  error,
  onCrear,
  onAnular,
  onActualizado,
}: GastosViewProps) {
  const [showForm, setShowForm] = useState(false);
  const [fecha, setFecha] = useState(() =>
    hoy(),
  );
  const [categoriaId, setCategoriaId] = useState('');
  // Sprint 17 S17-Z13: state numérico (null = vacío) en lugar de string.
  const [monto, setMonto] = useState<number | null>(null);
  const [proveedor, setProveedor] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [comprobanteUrl, setComprobanteUrl] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState<string>('todas');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [anulandoId, setAnulandoId] = useState<string | null>(null);
  // Sprint 17 S17-Z15: modal de anulación reemplaza window.prompt().
  const [gastoAAnular, setGastoAAnular] = useState<GastoRead | null>(null);
  // Sprint 17 Z21-fix: feedback visible si la anulación falla.
  const [errorAnular, setErrorAnular] = useState<string | null>(null);
  // Sprint 17 S17-Z14: modal de edición.
  const [gastoAEditar, setGastoAEditar] = useState<GastoRead | null>(null);

  const categoriasGasto = useMemo(
    () => categorias.filter((c) => c.tipo === 'gasto' && c.activa),
    [categorias],
  );

  function categoriaNombre(id: string): string {
    return categorias.find((c) => c.id === id)?.nombre || '—';
  }

  function categoriaColor(id: string): string {
    return categorias.find((c) => c.id === id)?.color || '#8a8479';
  }

  // KPIs: calculados en backend (services/dashboard_tesoreria.calcular_resumen_gastos)
  // para cuadrar con el dashboard — año contable activo, timezone Santiago,
  // exclusión de fondos de terceros. No se recalculan en cliente.
  const totalMes = resumen?.total_mes ?? 0;
  const totalAnio = resumen?.total_anio ?? 0;
  const anio = resumen?.anio_activo ?? null;
  const top3Mes = resumen?.top_categorias ?? [];

  // Gastos filtrados para la tabla (por categoría chip).
  const gastosFiltrados = useMemo(() => {
    if (filtroCategoria === 'todas') return gastos;
    return gastos.filter((g) => g.categoria_id === filtroCategoria);
  }, [gastos, filtroCategoria]);

  function resetForm() {
    setFecha(hoy());
    setCategoriaId('');
    setMonto(null);
    setProveedor('');
    setDescripcion('');
    setComprobanteUrl('');
    setFormError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!categoriaId) return setFormError('Selecciona una categoría');
    if (monto === null || monto <= 0)
      return setFormError('El monto debe ser mayor que 0');
    if (!proveedor.trim()) return setFormError('Proveedor requerido');
    if (!descripcion.trim()) return setFormError('Descripción requerida');
    setSubmitting(true);
    try {
      await onCrear({
        fecha,
        categoria_id: categoriaId,
        monto: monto,
        proveedor: proveedor.trim(),
        descripcion: descripcion.trim(),
        comprobante_url: comprobanteUrl.trim() || null,
      });
      resetForm();
      setShowForm(false);
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : 'Error al registrar',
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleAnular(gasto: GastoRead) {
    setGastoAAnular(gasto);
  }

  async function confirmarAnular(motivo: string) {
    if (!gastoAAnular) return;
    setAnulandoId(gastoAAnular.id);
    setErrorAnular(null);
    try {
      await onAnular(gastoAAnular.id, motivo);
      setGastoAAnular(null);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setErrorAnular(detail);
      } else {
        setErrorAnular('Error de red al anular');
      }
    } finally {
      setAnulandoId(null);
    }
  }

  return (
    <div className="px-4 md:px-8 py-6 space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-orange font-medium">
            Tesorería
          </div>
          <h1 className="text-2xl font-medium text-text-primary mt-1">
            Gastos
          </h1>
          <p className="text-sm text-text-secondary mt-1 max-w-2xl">
            Egresos del barrio. Los marcados con badge "COMP" son
            compensaciones (pago en especie, no afectan saldo bancario).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(!showForm)}
          className="bg-orange text-white px-4 py-2 rounded text-sm font-medium hover:opacity-90"
        >
          {showForm ? 'Cancelar' : '+ Nuevo gasto'}
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-text-muted">
              Total mes
            </div>
            <div className="text-2xl font-medium text-red-400 mt-2">
              {loading ? '—' : <Monto value={totalMes} />}
            </div>
            <div className="text-xs text-text-secondary mt-1">
              Gastos vigentes del mes en curso
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-text-muted">
              Total año {anio ?? '—'}
            </div>
            <div className="text-2xl font-medium text-red-400 mt-2">
              {loading ? '—' : <Monto value={totalAnio} />}
            </div>
            <div className="text-xs text-text-secondary mt-1">
              Acumulado año fiscal
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-text-muted">
              Top categorías del mes
            </div>
            {top3Mes.length === 0 ? (
              <div className="text-sm text-text-muted mt-3">Sin gastos este mes.</div>
            ) : (
              <ul className="mt-2 space-y-1">
                {top3Mes.map((t) => (
                  <li
                    key={t.categoria_id}
                    className="flex items-center gap-2 text-xs"
                  >
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: t.color }}
                    />
                    <span className="text-text-secondary flex-1 truncate">
                      {t.nombre}
                    </span>
                    <Monto value={t.monto} className="text-text-primary" />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>

      {/* Form */}
      {showForm && (
        <Card>
          <form
            onSubmit={handleSubmit}
            className="p-4 space-y-3"
            aria-label="Registrar nuevo gasto"
          >
            <div className="text-sm font-medium text-text-primary mb-2">
              Registrar nuevo gasto
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-muted mb-1">Fecha</label>
                <InputFecha
                  value={fecha}
                  onChange={setFecha}
                  className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
                />
              </div>
              <div>
                <label className="block text-xs text-text-muted mb-1">
                  Categoría
                </label>
                <CategoriaSelector
                  categorias={categoriasGasto}
                  value={categoriaId}
                  onChange={setCategoriaId}
                  placeholder="Seleccionar…"
                  className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
                />
              </div>
              <div>
                <label className="block text-xs text-text-muted mb-1">
                  Proveedor
                </label>
                <input
                  type="text"
                  value={proveedor}
                  onChange={(e) => setProveedor(e.target.value)}
                  placeholder="Ej. Aguas Andinas, Sodimac, Guillermo Pérez…"
                  className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
                />
              </div>
              <div>
                <label className="block text-xs text-text-muted mb-1">Monto</label>
                <InputMontoCLP
                  value={monto}
                  onChange={setMonto}
                  placeholder="0"
                  className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-text-muted mb-1">
                  Descripción
                </label>
                <input
                  type="text"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="Detalle del gasto…"
                  className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-text-muted mb-1">
                  URL del comprobante (opcional)
                </label>
                <input
                  type="url"
                  value={comprobanteUrl}
                  onChange={(e) => setComprobanteUrl(e.target.value)}
                  placeholder="https://…"
                  className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
                />
              </div>
            </div>
            {formError && (
              <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2">
                {formError}
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                disabled={submitting}
                className="px-3 py-2 text-sm text-text-secondary border border-white/[0.08] rounded hover:text-text-primary"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-3 py-2 text-sm font-medium bg-orange text-white rounded hover:opacity-90 disabled:opacity-40"
              >
                {submitting ? 'Registrando…' : 'Registrar gasto'}
              </button>
            </div>
          </form>
        </Card>
      )}

      {/* Chips filtros */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setFiltroCategoria('todas')}
          className={`text-xs px-3 py-1.5 rounded-full border ${
            filtroCategoria === 'todas'
              ? 'border-orange text-orange bg-orange/10'
              : 'border-white/[0.08] text-text-secondary hover:text-text-primary'
          }`}
        >
          Todas
          <Num className="ml-1.5 text-text-muted">{gastos.length}</Num>
        </button>
        {categoriasGasto.slice(0, 8).map((c) => {
          const count = gastos.filter((g) => g.categoria_id === c.id).length;
          if (count === 0) return null;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setFiltroCategoria(c.id)}
              className={`text-xs px-3 py-1.5 rounded-full border ${
                filtroCategoria === c.id
                  ? 'border-orange text-orange bg-orange/10'
                  : 'border-white/[0.08] text-text-secondary hover:text-text-primary'
              }`}
            >
              <span
                className="inline-block w-2 h-2 rounded-full mr-1.5"
                style={{ backgroundColor: c.color }}
              />
              {c.nombre}
              <Num className="ml-1.5 text-text-muted">{count}</Num>
            </button>
          );
        })}
      </div>

      {/* Tabla historial */}
      <Card>
        <div className="p-4">
          <div className="text-sm font-medium text-text-primary mb-3">
            Historial
          </div>
          {loading && <div className="text-sm text-text-muted">Cargando…</div>}
          {!loading && gastosFiltrados.length === 0 && (
            <div className="text-sm text-text-muted">
              {gastos.length === 0
                ? 'Sin gastos registrados. Usa "+ Nuevo gasto" para comenzar.'
                : 'Ningún gasto coincide con el filtro.'}
            </div>
          )}
          {!loading && gastosFiltrados.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-text-muted border-b border-white/[0.08]">
                    <th className="text-left font-medium py-2">Fecha</th>
                    <th className="text-left font-medium py-2">Proveedor</th>
                    <th className="text-left font-medium py-2">Descripción</th>
                    <th className="text-left font-medium py-2">Categoría</th>
                    <th className="text-right font-medium py-2">Monto</th>
                    <th className="text-right font-medium py-2 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {gastosFiltrados.map((g) => {
                    const compensado = g.compensacion_id !== null;
                    return (
                      <tr
                        key={g.id}
                        className={`border-b border-white/[0.04] ${
                          g.anulado ? 'opacity-50' : 'hover:bg-white/[0.02]'
                        }`}
                      >
                        <td className="py-2 text-text-secondary">
                          {formatFecha(g.fecha)}
                        </td>
                        <td className="py-2 text-text-primary truncate max-w-[140px]">
                          {g.proveedor}
                        </td>
                        <td className="py-2 text-text-secondary truncate max-w-xs">
                          {g.descripcion}
                          {compensado && (
                            <span className="ml-2 inline-block text-[9px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 font-semibold uppercase tracking-wide">
                              COMP
                            </span>
                          )}
                          {g.anulado && (
                            <span className="ml-2 text-red-400 text-[10px] uppercase tracking-wide">
                              · Anulado
                            </span>
                          )}
                        </td>
                        <td className="py-2 text-text-secondary">
                          <span className="flex items-center gap-1.5">
                            <span
                              className="inline-block w-2 h-2 rounded-full"
                              style={{
                                backgroundColor: categoriaColor(g.categoria_id),
                              }}
                            />
                            {categoriaNombre(g.categoria_id)}
                          </span>
                        </td>
                        <td className="py-2 text-right text-red-400 font-medium">
                          −<Monto value={g.monto} />
                        </td>
                        <td className="py-2 text-right">
                          {!g.anulado && (
                            <div className="flex items-center justify-end gap-3">
                              {/* Sprint 17 S17-Z14: gastos por compensación no son editables. */}
                              {!g.compensacion_id && (
                                <button
                                  type="button"
                                  onClick={() => setGastoAEditar(g)}
                                  className="text-[11px] text-text-secondary hover:text-orange"
                                >
                                  Editar
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleAnular(g)}
                                disabled={anulandoId === g.id}
                                className="text-[11px] text-text-secondary hover:text-state-danger disabled:opacity-40"
                              >
                                Anular
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {gastoAAnular && (
        <ModalConfirmar
          title="Anular gasto"
          mensaje={`Vas a anular el gasto "${gastoAAnular.descripcion.slice(0, 80)}" por ${formatCLP(gastoAAnular.monto)}. Esta acción registra la anulación con tu motivo y no se puede deshacer.`}
          pedirMotivo
          motivoPlaceholder="Explica brevemente por qué se anula este gasto…"
          motivoRequerido
          textoConfirmar="Sí, anular gasto"
          variantConfirmar="danger"
          onConfirmar={confirmarAnular}
          onCancelar={() => {
            setGastoAAnular(null);
            setErrorAnular(null);
          }}
          error={errorAnular}
        />
      )}

      {gastoAEditar && (
        <ModalEditarGasto
          gasto={gastoAEditar}
          categorias={categorias}
          onClose={() => setGastoAEditar(null)}
          onActualizado={(g) => {
            onActualizado(g);
            setGastoAEditar(null);
          }}
        />
      )}
    </div>
  );
}

export function GastosPage() {
  const { me } = useAuth();
  const [gastos, setGastos] = useState<GastoRead[]>([]);
  const [categorias, setCategorias] = useState<CategoriaRead[]>([]);
  const [resumen, setResumen] = useState<GastoResumen | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancel = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [g, c, r] = await Promise.all([
          listGastos({ incluir_anulados: true }),
          listCategorias({ activa: true }),
          getResumenGastos(),
        ]);
        if (cancel) return;
        setGastos(g);
        setCategorias(c);
        setResumen(r);
      } catch (e) {
        if (cancel) return;
        setError(e instanceof Error ? e.message : 'Error al cargar');
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    load();
    return () => {
      cancel = true;
    };
  }, [refreshKey]);

  if (!me) return null;

  async function handleCrear(input: {
    fecha: string;
    categoria_id: string;
    monto: number;
    proveedor: string;
    descripcion: string;
    comprobante_url: string | null;
  }) {
    // registrado_por_usuario_id lo deriva el backend del JWT.
    await crearGasto(input);
    setRefreshKey((k) => k + 1);
  }

  async function handleAnular(id: string, motivo: string) {
    await anularGasto(id, motivo);
    setRefreshKey((k) => k + 1);
  }

  function handleActualizado() {
    // Sprint 17 S17-Z14: refrescar lista tras edición. El modal ya cerró.
    setRefreshKey((k) => k + 1);
  }

  return (
    <GastosView
      gastos={gastos}
      categorias={categorias}
      resumen={resumen}
      loading={loading}
      error={error}
      onCrear={handleCrear}
      onAnular={handleAnular}
      onActualizado={handleActualizado}
    />
  );
}
